/**
 * Date Utilities for Financial Transactions (Frontend)
 * ===================================================
 *
 * This module ensures consistent date handling across the frontend.
 * All financial transactions should use dates at UTC noon (12:00:00) to avoid
 * timezone-related issues and ensure the date remains consistent across all timezones.
 */

import dayjs from "dayjs";

const pad2 = (n) => String(n).padStart(2, "0");

/**
 * BigQuery streaming date window
 * ------------------------------
 * `transactions.journal_entries` is DAY-partitioned on `date`, and streaming
 * inserts into a column-partitioned table only accept partitions from 5 years
 * in the past through 1 year in the future. A row outside that window saves to
 * Firestore cleanly and then fails every BigQuery sync forever — three retries,
 * an error doc, five repair attempts, quarantine — and never appears in a
 * single report (GH#344).
 *
 * This is a platform constraint, not a form or import concern, which is why it
 * lives here rather than in either caller. Callers keep their own user-facing
 * message: an import wants the "mistyped year" hint, the entry form wants the
 * "cannot be reported on" hint.
 *
 * One other copy exists and must move together with this one:
 * `functions/transactions/jeWriteGuards.js` — the ENFORCING copy (backend, and
 * deliberately dayjs-free/UTC). No module may span `functions/` and `src/`;
 * `src/utilities/bigQueryDateWindow.sync.test.js` pins the two together.
 */
export const BQ_STREAMING_WINDOW_YEARS_PAST = 5;
export const BQ_STREAMING_WINDOW_YEARS_FUTURE = 1;

/** Inclusive bounds, as dayjs objects, as of `now`. */
export const bigQueryDateWindow = (now = dayjs()) => ({
  min: now.subtract(BQ_STREAMING_WINDOW_YEARS_PAST, "year").startOf("day"),
  max: now.add(BQ_STREAMING_WINDOW_YEARS_FUTURE, "year").endOf("day"),
});

/**
 * True when a date falls inside the window BigQuery will accept.
 *
 * An absent or unparseable date returns TRUE — the surrounding "date is
 * required" / "could not parse this date" validation owns that case, and
 * restating it as a range problem would only mislead. Same rule as the backend
 * guard's inverse predicate.
 *
 * @param {Object|Date|string|null} date - dayjs object, Firestore Timestamp,
 *   Date, or date string
 * @return {boolean}
 */
export const isWithinBigQueryDateWindow = (date) => {
  if (!date) return true;
  const value = dayjs.isDayjs(date)
    ? date
    : dayjs(typeof date?.toDate === "function" ? date.toDate() : date);
  if (!value.isValid()) return true;
  const { min, max } = bigQueryDateWindow();
  return !value.isBefore(min) && !value.isAfter(max);
};

/**
 * Coerce any date shape this codebase passes around — ISO string, dayjs,
 * Firestore Timestamp (`toDate` / `seconds` / `_seconds`), or a native Date —
 * into a YYYY-MM-DD calendar day. Returns null on falsy or unrecognized input.
 * Mirror of normalizeIsoDate in functions/reports/sharing/packageSchema.js.
 *
 * Dates are stored at UTC noon, so UTC getters are the correct read for
 * everything except dayjs (which is local and formats itself). The dayjs branch
 * MUST stay ahead of the `toDate` branch: dayjs objects have a `toDate()` too,
 * and going through it re-projects local midnight into UTC — sliding
 * dayjs("2026-01-01") back to 2025-12-31 east of UTC and silently inventing a
 * tax-year change.
 */
export function toIsoDateString(v) {
  if (!v) return null;
  if (typeof v === "string") return v.slice(0, 10) || null;
  if (typeof v.format === "function") return v.format("YYYY-MM-DD");
  let date = null;
  if (typeof v.toDate === "function") date = v.toDate();
  else if (v instanceof Date) date = v;
  else if (typeof v.seconds === "number") date = new Date(v.seconds * 1000);
  else if (typeof v._seconds === "number") date = new Date(v._seconds * 1000);
  if (!date || Number.isNaN(date.getTime())) return null;
  return `${date.getUTCFullYear()}-${pad2(date.getUTCMonth() + 1)}-${pad2(date.getUTCDate())}`;
}

/**
 * Creates a timezone-agnostic date at UTC noon for financial transactions
 * @param {string|Date|Object} dateInput - The date in various formats (including dayjs objects)
 * @returns {Date} A Date object set to noon UTC (12:00:00) for the given date
 */
export function createFinancialDate(dateInput) {
  if (!dateInput) {
    throw new Error("Date input is required");
  }

  let year, month, day;

  if (typeof dateInput === "string") {
    // Handle ISO format and common date strings
    const parsed = new Date(dateInput);
    if (isNaN(parsed.getTime())) {
      throw new Error(`Invalid date string: ${dateInput}`);
    }
    year = parsed.getUTCFullYear();
    month = parsed.getUTCMonth();
    day = parsed.getUTCDate();
  } else if (dateInput instanceof Date) {
    if (isNaN(dateInput.getTime())) {
      throw new Error("Invalid Date object");
    }
    year = dateInput.getUTCFullYear();
    month = dateInput.getUTCMonth();
    day = dateInput.getUTCDate();
  } else if (dayjs.isDayjs(dateInput)) {
    // Handle dayjs objects - dayjs handles UTC properly
    year = dateInput.year();
    month = dateInput.month();
    day = dateInput.date();
  } else if (dateInput && typeof dateInput.toDate === "function") {
    // Firestore timestamp
    const date = dateInput.toDate();
    year = date.getUTCFullYear();
    month = date.getUTCMonth();
    day = date.getUTCDate();
  } else if (dateInput && typeof dateInput.seconds === "number") {
    // Firestore timestamp (alternative format)
    const date = new Date(dateInput.seconds * 1000);
    year = date.getUTCFullYear();
    month = date.getUTCMonth();
    day = date.getUTCDate();
  } else {
    throw new Error(`Unsupported date input type: ${typeof dateInput}`);
  }

  // Create date at UTC noon (12:00:00) to ensure it stays on the same calendar day
  // across all timezones (UTC-12 to UTC+14)
  return new Date(Date.UTC(year, month, day, 12, 0, 0, 0));
}

/**
 * Formats a date as YYYY-MM-DD string
 * @param {Date} date - The date to format
 * @returns {string} Date formatted as YYYY-MM-DD
 */
export function formatDateForDisplay(date) {
  if (!(date instanceof Date)) {
    throw new Error("Input must be a Date object");
  }

  const year = date.getUTCFullYear();
  const month = String(date.getUTCMonth() + 1).padStart(2, "0");
  const day = String(date.getUTCDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
}

/**
 * Creates a date range for querying a specific day
 * @param {string} dateString - The date in YYYY-MM-DD format
 * @returns {Object} Object with startDate and endDate for the full day in UTC
 */
export function createDateRangeForDay(dateString) {
  const date = createFinancialDate(dateString);

  // Start of day (already at midnight)
  const startDate = new Date(date);

  // End of day (23:59:59.999)
  const endDate = new Date(
    Date.UTC(
      date.getUTCFullYear(),
      date.getUTCMonth(),
      date.getUTCDate(),
      23,
      59,
      59,
      999,
    ),
  );

  return { startDate, endDate };
}

/**
 * Converts a dayjs object to a financial date
 * @param {Object} dayjsObject - A dayjs object
 * @returns {Date} A Date object set to noon UTC
 */
export function dayjsToFinancialDate(dayjsObject) {
  if (!dayjs.isDayjs(dayjsObject)) {
    throw new Error("Input must be a dayjs object");
  }
  return createFinancialDate(dayjsObject);
}
